import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "pn_install_dismissed";

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = window.navigator.userAgent || "";
  const iDevice = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iDevice || iPadOS;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null); // Android/desktop beforeinstallprompt event
  const [visible, setVisible] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (sessionStorage.getItem(DISMISS_KEY)) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari never fires beforeinstallprompt — surface a manual hint instead.
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => setVisible(true), 1500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    setShowIosSheet(false);
    sessionStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice && choice.outcome === "accepted") {
        setVisible(false);
      }
      setDeferred(null);
    } else if (isIOS()) {
      setShowIosSheet(true);
    }
  };

  if (!visible) return null;

  return (
    <>
      <AnimatePresence>
        <motion.div
          key="install-pill"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 right-4 z-[60] max-w-[92vw]"
          data-testid="install-app-banner"
        >
          <div className="force-dark flex items-center gap-3 rounded-full border border-white/15 bg-[#141414] pl-4 pr-2 py-2 shadow-2xl">
            <Download className="h-4 w-4 text-[#E2E30A] shrink-0" />
            <p className="text-sm text-white whitespace-nowrap">Install the app</p>
            <button
              type="button"
              onClick={install}
              data-testid="install-app-btn"
              className="rounded-full bg-[#E2E30A] px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-black hover:bg-[#c9ca09] transition-colors"
            >
              Add
            </button>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss install"
              data-testid="install-dismiss-btn"
              className="rounded-full p-1.5 text-zinc-400 hover:text-white transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        {showIosSheet && (
          <motion.div
            key="ios-sheet"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={dismiss}
            data-testid="ios-install-sheet"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="force-dark w-full max-w-sm rounded-2xl border border-white/15 bg-[#141414] p-6 text-white"
            >
              <p className="font-display text-xl uppercase tracking-tight">Add to Home Screen</p>
              <p className="text-sm text-zinc-300 mt-2">Install Performance Nutrition on your iPhone in two taps:</p>
              <ol className="mt-4 space-y-3 text-sm text-zinc-200">
                <li className="flex items-center gap-3">
                  <Share className="h-5 w-5 text-[#4C9AFF] shrink-0" />
                  Tap the <span className="font-semibold">Share</span> button in Safari
                </li>
                <li className="flex items-center gap-3">
                  <Plus className="h-5 w-5 text-[#4C9AFF] shrink-0" />
                  Choose <span className="font-semibold">Add to Home Screen</span>
                </li>
              </ol>
              <button
                type="button"
                onClick={dismiss}
                className="mt-6 w-full rounded-full bg-[#E2E30A] py-3 text-sm font-bold uppercase tracking-wider text-black hover:bg-[#c9ca09] transition-colors"
                data-testid="ios-sheet-close-btn"
              >
                Got it
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
